/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';

import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {NetworkApiService, NetworkModel} from '../../admin/network/network-api.service';
import template from './network-overview-page.component.html';
import {SIMPLE_PAGE_IMPORTS} from './simple-page-imports';

const PAGE_SIZE = 1001;

@Component({
  standalone: true,
  imports: SIMPLE_PAGE_IMPORTS,
  selector: 'adn-network-overview-page',
  template
})
export class NetworkOverviewPageComponent implements OnInit, OnDestroy {
  readonly allowed: boolean;
  readonly networks = signal<NetworkModel[]>([]);
  readonly loading = signal(false);
  readonly loadFailed = signal(false);

  searchText = '';
  private destroyed = false;

  constructor(
    @Inject(NetworkApiService) private readonly networkApi: NetworkApiService,
    @Inject(LocalUserProfile) localUserProfile: LocalUserProfile
  ) {
    this.allowed = localUserProfile.isSpecialUser();
  }

  ngOnInit(): void {
    if (this.allowed) void this.loadNetworks();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  get filteredNetworks(): NetworkModel[] {
    const searchText = this.searchText.trim().toLowerCase();
    if (!searchText) return this.networks();
    return this.networks().filter((network) => [network.name, network.id, network.tagId]
      .some((value) => String(value || '').toLowerCase().includes(searchText)));
  }

  async loadNetworks(): Promise<void> {
    if (!this.allowed || this.loading()) return;

    this.loading.set(true);
    this.loadFailed.set(false);
    try {
      const networks: NetworkModel[] = [];
      let page = 1;
      let totalCount = 0;
      do {
        const result = await firstValueFrom(this.networkApi.list({
          includeActive: true,
          includeInactive: true,
          includeHidden: true,
          orderBy: 'name',
          orderByDirection: 'ASCENDING',
          page,
          pageSize: PAGE_SIZE,
          concise: false
        }));
        const results = result?.results || [];
        networks.push(...results);
        totalCount = result?.totalCount || networks.length;
        page++;
        if (!results.length) break;
      } while (networks.length < totalCount);

      if (!this.destroyed) {
        networks.sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
        this.networks.set(networks);
      }
    } catch {
      if (!this.destroyed) {
        this.networks.set([]);
        this.loadFailed.set(true);
      }
    } finally {
      if (!this.destroyed) this.loading.set(false);
    }
  }

  marketplaceLabel(network: NetworkModel): string {
    if (network.marketplacePlatform) return 'Marketplace';
    if (network.hybridMarketplacePlatform) return 'Hybrid';
    return 'No';
  }

  platformLabels(network: NetworkModel): string[] {
    const platforms: string[] = [];
    if (network.advertisingPlatform) platforms.push('Advertising');
    if (network.dataPlatform) platforms.push('Data');
    if (network.suiPlatform) platforms.push('Self-service');
    if (network.politicalApp) platforms.push('Political');
    return platforms;
  }

  currencyLabels(network: NetworkModel): string[] {
    return Array.from(new Set([
      network.defaultCurrency,
      ...(network.availableCurrencies || [])
    ].filter(Boolean)));
  }
}
